import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { portfolioService } from '@/lib/api/services/portfolio.service';
import type { Tables, TablesInsert, TablesUpdate } from '@/lib/supabase/supabase-types';

type PortfolioProject = Tables<'portfolio_projects'>;
type PortfolioProjectInsert = TablesInsert<'portfolio_projects'>;
type PortfolioProjectUpdate = TablesUpdate<'portfolio_projects'>;

export function usePortfolioProjects(freelancerProfileId?: string) {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const queryKey = ['portfolio-projects', freelancerProfileId];

  // Fetch portfolio projects
  const {
    data: projects = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      // If no profile ID provided, get current user's projects via API
      if (!freelancerProfileId) {
        return await portfolioService.getMyProjects();
      }

      // Use backend API to get projects by profile ID
      return await portfolioService.getProjectsByProfile(freelancerProfileId);
    },
    enabled: !!freelancerProfileId || !!user?.id,
  });

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: async (data: Omit<PortfolioProjectInsert, 'freelancer_profile_id'>) => {
      // Use API for creating projects
      return await portfolioService.createProject(data);
    },
    onMutate: async (newProject) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousProjects = queryClient.getQueryData<PortfolioProject[]>(queryKey) || [];

      // Optimistically update
      const optimisticProject: PortfolioProject = {
        id: `temp-${Date.now()}`,
        freelancer_profile_id: freelancerProfileId!,
        display_order: Math.max(...previousProjects.map(p => p.display_order), -1) + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        title: newProject.title,
        description: newProject.description,
        image_url: newProject.image_url || null,
        project_url: newProject.project_url || null,
      };

      queryClient.setQueryData(queryKey, [...previousProjects, optimisticProject]);

      return { previousProjects, optimisticProject };
    },
    onError: (error, _newProject, context) => {
      // Rollback on error
      if (context?.previousProjects) {
        queryClient.setQueryData(queryKey, context.previousProjects);
      }
      toast.error('Failed to create project');
      console.error('Error creating project:', error);
    },
    onSuccess: (data, _variables, context) => {
      // Replace optimistic update with real data
      const previousProjects = context?.previousProjects || [];
      const updatedProjects = previousProjects.filter(p => p.id !== context?.optimisticProject.id);
      queryClient.setQueryData(queryKey, [...updatedProjects, data].sort((a, b) => a.display_order - b.display_order));
      toast.success('Project added successfully');
    },
  });

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: PortfolioProjectUpdate }) => {
      // Use API for updating projects
      return await portfolioService.updateProject(id, updates);
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousProjects = queryClient.getQueryData<PortfolioProject[]>(queryKey) || [];

      // Optimistically update
      const updatedProjects = previousProjects.map(project =>
        project.id === id
          ? { ...project, ...updates, updated_at: new Date().toISOString() }
          : project
      );

      queryClient.setQueryData(queryKey, updatedProjects);

      return { previousProjects };
    },
    onError: (error, _variables, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(queryKey, context.previousProjects);
      }
      toast.error('Failed to update project');
      console.error('Error updating project:', error);
    },
    onSuccess: (data) => {
      // Update with real data
      const currentProjects = queryClient.getQueryData<PortfolioProject[]>(queryKey) || [];
      const updatedProjects = currentProjects.map(project =>
        project.id === data.id ? data : project
      );
      queryClient.setQueryData(queryKey, updatedProjects);
      toast.success('Project updated successfully');
    },
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: async (id: string) => {
      // Use API for deleting projects
      await portfolioService.deleteProject(id);
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });

      const previousProjects = queryClient.getQueryData<PortfolioProject[]>(queryKey) || [];

      // Optimistically remove
      const updatedProjects = previousProjects.filter(project => project.id !== id);
      queryClient.setQueryData(queryKey, updatedProjects);

      return { previousProjects, deletedProject: previousProjects.find(p => p.id === id) };
    },
    onError: (error, _id, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(queryKey, context.previousProjects);
      }
      toast.error('Failed to delete project');
      console.error('Error deleting project:', error);
    },
    onSuccess: () => {
      toast.success('Project deleted successfully');
    },
  });

  // Reorder projects mutation
  const reorderProjectsMutation = useMutation({
    mutationFn: async (reorderedProjects: PortfolioProject[]) => {
      // Update display_order for each project using API
      const updates = reorderedProjects.map((project, index) => ({
        id: project.id,
        display_order: index,
      }));

      // Update each project individually via API
      for (const update of updates) {
        await portfolioService.updateProject(update.id, { display_order: update.display_order });
      }

      return reorderedProjects;
    },
    onMutate: async (reorderedProjects) => {
      await queryClient.cancelQueries({ queryKey });

      const previousProjects = queryClient.getQueryData<PortfolioProject[]>(queryKey) || [];

      // Optimistically update order
      const updatedProjects = reorderedProjects.map((project, index) => ({
        ...project,
        display_order: index,
        updated_at: new Date().toISOString(),
      }));

      queryClient.setQueryData(queryKey, updatedProjects);

      return { previousProjects };
    },
    onError: (error, _variables, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(queryKey, context.previousProjects);
      }
      toast.error('Failed to reorder projects');
      console.error('Error reordering projects:', error);
    },
    onSuccess: () => {
      toast.success('Projects reordered successfully');
    },
  });

  return {
    projects,
    isLoading,
    error,

    // Mutation functions
    createProject: createProjectMutation.mutateAsync,
    updateProject: ({ id, ...updates }: { id: string } & PortfolioProjectUpdate) =>
      updateProjectMutation.mutateAsync({ id, updates }),
    deleteProject: deleteProjectMutation.mutateAsync,
    reorderProjects: reorderProjectsMutation.mutateAsync,

    // Mutation states
    isCreating: createProjectMutation.isPending,
    isUpdating: updateProjectMutation.isPending,
    isDeleting: deleteProjectMutation.isPending,
    isReordering: reorderProjectsMutation.isPending,
  };
}
